import { getComparableRecordDate } from "@/util/date.ts";

/**
 * Given number values for 4 input fields representing hours, minutes, seconds, and milliseconds,
 * it returns a string representing the total number of seconds to be stored in the database
 *
 * @param {String}  str string of the 4 input fields in the format -- HH:MM:SS:SSS
 *  * @returns a string representing the total number of seconds
 *
 */

const recordTimeToSeconds = (str) => {
  if (!str) return "0.0";
  const p = str.split(":");
  let s = 0;
  let m = 1;
  if (p.length === 4) {
    const decimal = p[3];
    p.pop();
    while (p.length > 0) {
      s += m * parseInt(p.pop(), 10);
      m *= 60;
    }
    const returnValue = `${s.toString()}.${decimal}`;
    return returnValue;
  }
  return "0.0";
};

/**
 * Given the number of total seconds, returns integer values for 4 input fields representing
 * hours, minutes, seconds, and millisecondss,
 * i
 *
 * @param {String}  totalSeconds the total number of seconds in a time recordValue.
 * @returns a string representing the 4 input fields in the format -- HH:mm:ss:SS
 *
 */

const recordSecondsToTime = (totalSeconds) => {
  if (totalSeconds >= 0 && totalSeconds !== "" && totalSeconds != null) {
    let [seconds, milliseconds] = `${totalSeconds}`.split(".");
    seconds = parseInt(seconds, 10);
    milliseconds = milliseconds
      ? milliseconds.padEnd(2, "0").slice(0, 2)
      : "00";

    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;
    const minutes = Math.floor(seconds / 60);
    seconds %= 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}.${milliseconds}`;
  }
  return "00:00:00.00";
};

function formatPresentationalRecordTime(possiblyTime) {
  if (possiblyTime.split(":").length !== 3) {
    return possiblyTime;
  }

  const [hours, minutes, seconds] = possiblyTime.split(":");
  const [secs, millis] = seconds.split(".");
  const milliseconds = millis?.padEnd(2, "0") ?? "00";

  if (hours === "00" && minutes === "00") {
    return `${secs}.${milliseconds}`;
  }

  if (hours === "00") {
    return `${minutes}:${secs}.${milliseconds}`;
  }

  return `${hours}:${minutes}:${secs}.${milliseconds}`;
}

function getWinPercentage(winLossString) {
  if (!winLossString) {
    return 0;
  }
  const parts = winLossString.split("-");
  const wins = Number(parts[0]);
  const losses = Number(parts[1]);
  const draws = Number(parts[2]) || 0;

  const totalGames = wins + losses + draws;
  const totalPoints = wins + 0.5 * draws; // Assign half value to draws
  const percentage = totalPoints / totalGames;
  return parseFloat(percentage).toFixed(4);
}

// sort descending by default
function recordSortWinLoss(a, b) {
  const winPercentageA = getWinPercentage(a.recordInfo.recordValue);
  const winPercentageB = getWinPercentage(b.recordInfo.recordValue);
  return winPercentageB - winPercentageA;
}

// sort descending by default
function recordSortDefault(a, b) {
  return b.recordInfo.recordValue - a.recordInfo.recordValue;
}

// sort descending by default
function recordSortNewest(a, b) {
  return getComparableRecordDate(b) - getComparableRecordDate(a);
}

function recordSortCustom(a, b) {
  return a.recordInfo.customOrderIndex - b.recordInfo.customOrderIndex;
}

function sortRecords(column, records) {
  const sortBy = {
    WinLoss: () => [...records].sort(recordSortWinLoss),
    Date: () => [...records].sort(recordSortNewest),
    Default: () => [...records].sort(recordSortDefault),
    Custom: () => [...records].sort(recordSortCustom),
  };
  const isDateSort =
    column.recordSortDirection === "oldest" ||
    column.recordSortDirection === "newest";
  const isAscending =
    column.recordSortDirection === "Ascending" ||
    column.recordSortDirection === "oldest";
  const isCustom = column.recordSortDirection === "custom";

  let sortKey = "Default";

  if (isDateSort) {
    sortKey = "Date";
  } else if (isCustom) {
    sortKey = "Custom";
  } else if (column.recordType === "WinLoss") {
    sortKey = "WinLoss";
  }

  const sortFunction = sortBy[sortKey];

  if (isAscending) {
    return sortFunction().toReversed();
  }

  return sortFunction();
}

function convertImperial(value) {
  const feet = Math.floor(value / 12);
  const inches = value - feet * 12;
  const inchesFormatted = +inches.toFixed(2);
  return `${feet}' ${inchesFormatted}"`;
}

function convertWinLoss(value) {
  return value || "0 - 0";
}

function formatRecordValue(recordType, recordValue) {
  switch (recordType) {
    case "Time":
      return recordSecondsToTime(recordValue);
    case "Imperial":
      return convertImperial(recordValue);
    case "WinLoss":
      return convertWinLoss(recordValue);
    default:
      // Number
      return new Intl.NumberFormat("en-US").format(recordValue);
  }
}

function formatDateRange(yearStart, yearEnd) {
  if (yearStart && yearEnd) {
    return `${yearStart} - ${yearEnd}`;
  }
  return "";
}

function recordInchesToImperial(inches) {
  const feet = Math.floor(inches / 12);
  const remainderInches = inches % 12;
  return { feet, inches: remainderInches };
}

function recordImperialToInches(feet, inches) {
  return parseInt(feet, 10) * 12 + parseFloat(inches);
}

function recordWinLossToValues(recordValue) {
  if (!recordValue) {
    return { wins: 0, losses: 0, draws: 0 };
  }

  const parts = recordValue.split(" - ").map(Number);
  const wins = parts[0] || 0;
  const losses = parts[1] || 0;
  const draws = parts[2] || 0;

  return { wins, losses, draws };
}

function recordValuesToWinLoss(wins, losses, draws) {
  return `${wins} - ${losses} - ${draws}`;
}

// Utility to extract year as a number from 'YYYY' or 'MMMM D, YYYY'
function extractYear(yearString) {
  if (!yearString) return 0;
  // If it's a 4-digit year
  const yearMatch = yearString.match(/\b(\d{4})\b/);
  if (yearMatch) return parseInt(yearMatch[1], 10);
  return 0;
}

/**
 * Groups records by value and sorts ties by year (unless sort direction is 'custom' or 'oldest').
 * @param {Object} collection - The collection object with records and recordSortDirection
 * @returns {Array} Array of grouped records
 */
function groupAndSortRecords(collection) {
  const sortedRecords = collection.records;
  const groups = [];
  let currentGroup = null;
  let currentValue = null;
  sortedRecords.forEach((record) => {
    if (record.value !== currentValue) {
      currentValue = record.value;
      currentGroup = {
        value: currentValue,
        records: [],
      };
      groups.push(currentGroup);
    }
    currentGroup.records.push(record);
  });
  // groups represent ties
  groups.forEach((group) => {
    // sort ties by descending year unless custom or oldest sort
    if (
      group.records.length > 1 &&
      !["custom", "oldest"].includes(collection.recordSortDirection)
    ) {
      group.records.sort((a, b) => {
        const getYear = (rec) => {
          if (rec.year) return extractYear(rec.year);
          if (rec.yearRange) {
            // Try to extract the second year from 'YYYY-YYYY' or 'YYYY - YYYY'
            const match = rec.yearRange.match(
              /\b(\d{4})\b\s*[-–—]\s*\b(\d{4})\b/
            );
            if (match && match[2]) return parseInt(match[2], 10);
          }
          return 0;
        };
        return getYear(b) - getYear(a);
      });
    }
  });
  return groups;
}

export {
  recordTimeToSeconds,
  recordSecondsToTime,
  recordSortWinLoss,
  recordSortDefault,
  getWinPercentage,
  sortRecords,
  formatRecordValue,
  formatDateRange,
  recordInchesToImperial,
  recordImperialToInches,
  recordWinLossToValues,
  recordValuesToWinLoss,
  formatPresentationalRecordTime,
  groupAndSortRecords,
};
